import { useEffect } from "react";

import { isEmpty } from "lodash/fp";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import {
  SearchOptions,
  searchNotes,
  searchTranscript,
} from "@app/analytics/MeetingReport/utils/searchUtils";
import useAuth from "@app/common/auth/useAuth";
import { withoutQueryParams, withQueryParams } from "@app/common/utils";

import useMeetingReportInitialLoading from "./useMeetingReportInitialLoading";
import useMeetingReportSearch from "./useMeetingReportSearch";
import useMeetingReportSessionNotes from "./useMeetingReportSessionNotes";
import useSessionMetadata from "./useSessionMetadata";
import useTranscript from "./useTranscript";

const useSearch = () => {
  // TODO: ENG-8597 Remove after BE supports unauthenticated searches for public sessions
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [queryParams] = useSearchParams();
  const { keywords, summary, turns } = useTranscript();

  const { inEditMode, editedTurns, editedNotes } = useEditStore((state) => ({
    inEditMode: state.editingSection != null,
    editedTurns: state.transcript.turns,
    editedNotes: state.notes,
  }));

  const { notes: sessionNotes } = useMeetingReportSessionNotes();
  const { isUnauthorized } = useMeetingReportInitialLoading();
  const { data: sessionMetadata } = useSessionMetadata({
    enabled: !isUnauthorized,
  });

  const searchDisabled =
    isUnauthorized ||
    sessionMetadata.isRestrictedReport ||
    sessionMetadata.isProcessing;
  const queryParamSearch = queryParams.get("search");

  const {
    searchMode,
    setSearchMode,
    resetState,
    searchResults,
    searchTerm,
    setSearchResults,
    startSearch,
  } = useSearchStore((state) => ({
    searchMode: state.searchMode,
    setSearchMode: state.setSearchMode,
    resetState: state.resetState,
    searchResults: state.searchResults,
    searchTerm: state.searchTerm,
    setSearchResults: state.setSearchResults,
    startSearch: state.startSearch,
  }));

  // Meeting report search uses the BE search
  // which we dont want when in replace mode
  useMeetingReportSearch({
    enabled: !searchDisabled && searchMode !== "replace",
  });

  const performSearch = (term: string, opts?: SearchOptions) => {
    if (searchDisabled) return;

    const searchType =
      opts?.searchType ?? (!user || opts?.exactPhrase ? "manual" : "default");

    startSearch(term, searchType);

    if (opts?.appendQueryParam ?? true) {
      const currentSearchParams = new URLSearchParams(location.search);
      const currentSearchTerm = currentSearchParams.get("search");
      // Prevent noop navigations from callers that don't know if the search term has changed
      // otherwise react-router will render the whole route needlessly
      if (
        currentSearchTerm !== term &&
        !(isEmpty(term) && isEmpty(currentSearchTerm))
      ) {
        navigate(
          withQueryParams(location.pathname, { search: term }, location.search),
          { replace: true }
        );
      }
    }

    if (searchType !== "manual") return;

    const shouldSearchNotes = opts?.searchNotes ?? true;
    const shouldSearchTranscript =
      !sessionMetadata.isNotetakerReport && (opts?.searchTranscript ?? true);

    const summaryToSearch = inEditMode ? editedNotes : summary;
    const sessionNotesToSearch = inEditMode ? editedNotes.notes : sessionNotes;

    const noteResults = shouldSearchNotes
      ? searchNotes(term, summaryToSearch, sessionNotesToSearch, opts)
      : [];

    const turnsToSearch = inEditMode ? editedTurns : turns;

    const transcriptResults = shouldSearchTranscript
      ? searchTranscript(
          term,
          turnsToSearch,
          keywords,
          noteResults.length,
          opts
        )
      : [];

    setSearchResults([...noteResults, ...transcriptResults]);
  };

  useEffect(() => {
    if (searchTerm || searchDisabled) return;

    // perform initial search if page is initialized with a search query param
    if (queryParamSearch) {
      performSearch(queryParamSearch);
    }
  }, [queryParamSearch, searchTerm, searchDisabled]);

  const clearSearch = (preserveSearchMode = true) => {
    resetState();

    // preserve the search mode so we don't close the search box
    if (preserveSearchMode) {
      setSearchMode(searchMode);
    }

    navigate(
      withoutQueryParams(location.pathname, location.search, ["search"]),
      { replace: true }
    );
  };

  return {
    searchMode,
    setSearchMode,
    clearSearch,
    performSearch,
    results: searchResults,
    term: searchTerm,
  };
};

export default useSearch;
